// @ts-check
/**
 * Delegated handler for `[data-action="back"]` buttons. Lets HTML use
 * `<button data-action="back">` instead of inline `onclick=` JS, removing
 * script-load-order coupling.
 *
 * Behaviour: navigate to `/club/<clubCode>` if a club code is stored, else `/`.
 * Override per-button with `data-back-href="/some/path"`.
 *
 * Also installs a swipe-from-left-edge gesture that triggers the same
 * navigation, so PWAs in standalone mode (where the browser's swipe-back
 * gesture is unavailable) still have a way to navigate back. Disable per
 * page by setting `data-no-swipe-back` on `<body>`.
 *
 * Call {@link initBackButton} once per page (idempotent).
 */

import { getClubCode } from "./auth-context.js";

let initialised = false;

/**
 * Resolve the back destination for the current page. Prefers the
 * `data-back-href` attribute on a `[data-action="back"]` button if present,
 * so the swipe gesture matches the visible back button.
 *
 * @returns {string}
 */
function resolveBackHref() {
  const btn = /** @type {HTMLElement | null} */ (
    document.querySelector('[data-action="back"]')
  );
  const override = btn?.dataset.backHref;
  if (override) return override;
  const club = getClubCode();
  return club ? `/club/${encodeURIComponent(club)}` : "/";
}

/**
 * Listen for a horizontal swipe that starts within the left edge of the
 * viewport and triggers a navigation back. Mirrors the iOS/Android system
 * gesture for users on installed PWAs.
 */
function initSwipeBack() {
  if (document.body?.dataset.noSwipeBack !== undefined) return;

  const EDGE_PX = 24; // start zone from the left edge
  const MIN_DX = 60; // minimum horizontal travel
  const MAX_VERTICAL_RATIO = 0.5; // dy/dx must be below this

  /** @type {{ x: number, y: number, t: number } | null} */
  let start = null;

  document.addEventListener(
    "touchstart",
    (ev) => {
      if (!ev.touches || ev.touches.length !== 1) {
        start = null;
        return;
      }
      const t = ev.touches[0];
      if (t.clientX > EDGE_PX) {
        start = null;
        return;
      }
      // Skip when the touch starts inside a horizontally scrollable element
      // (e.g. a carousel) so we don't fight legitimate scrolling.
      const target = /** @type {Element | null} */ (ev.target);
      if (target?.closest('[data-no-swipe-back], input, textarea, [contenteditable="true"]')) {
        start = null;
        return;
      }
      start = { x: t.clientX, y: t.clientY, t: Date.now() };
    },
    { passive: true }
  );

  document.addEventListener(
    "touchend",
    (ev) => {
      if (!start) return;
      const touch = ev.changedTouches?.[0];
      if (!touch) {
        start = null;
        return;
      }
      const dx = touch.clientX - start.x;
      const dy = Math.abs(touch.clientY - start.y);
      const dt = Date.now() - start.t;
      start = null;
      if (dt > 800) return; // too slow to be an intentional gesture
      if (dx < MIN_DX) return;
      if (dx > 0 && dy / dx > MAX_VERTICAL_RATIO) return;
      window.location.href = resolveBackHref();
    },
    { passive: true }
  );
}

export function initBackButton() {
  if (initialised) return;
  initialised = true;
  document.addEventListener("click", (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (!target) return;
    const button = target.closest('[data-action="back"]');
    if (!button) return;
    event.preventDefault();
    const override = /** @type {HTMLElement} */ (button).dataset.backHref;
    if (override) {
      window.location.href = override;
      return;
    }
    const club = getClubCode();
    window.location.href = club ? `/club/${encodeURIComponent(club)}` : "/";
  });
  initSwipeBack();
}

